import { useState, useEffect, useCallback } from 'react';
import { getWorkingHours, updateWorkingHours } from '../../api/workingHours';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { WorkingHoursDayRow } from '../../components/branches/WorkingHoursDayRow';

// UI görsel sırası Pzt-Paz; day_of_week numarası (0=Paz...6=Cmt) korunur.
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

/** "HH:MM:SS" / "HH:MM" → "HH:MM" (karşılaştırma + gönderim normalizasyonu). */
function toHHMM(v) {
  return v ? String(v).slice(0, 5) : '';
}

/** is_working_day / start / end farkı (grace_minutes ve id hariç). */
function dayChanged(a, b) {
  return (
    a.is_working_day !== b.is_working_day ||
    toHHMM(a.start_time) !== toHHMM(b.start_time) ||
    toHHMM(a.end_time) !== toHHMM(b.end_time)
  );
}

export function WorkingHoursPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [days, setDays] = useState([]);
  const [original, setOriginal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError('');
    getWorkingHours()
      .then((d) => { setDays(d.working_hours); setOriginal(d.working_hours); })
      .catch((err) => setLoadError(err.response?.data?.error || 'Çalışma saatleri yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Row değişikliği → state'i day_of_week ile eşle (id ile DEĞİL — id null olabilir).
  const handleDayChange = (updatedDay) => {
    setDays((prev) => prev.map((d) => (d.day_of_week === updatedDay.day_of_week ? updatedDay : d)));
    setSaveError('');
    setSaveOk(false);
  };

  // Orijinale göre değişen günler — yalnız bunlar PATCH'e gider (subset).
  const changed = days.filter((d) => {
    const orig = original.find((o) => o.day_of_week === d.day_of_week);
    return orig && dayChanged(d, orig);
  });

  const handleSave = async () => {
    // updates[] — grace_minutes KOYULMAZ (FARK 1: backend keep-existing korur).
    // is_working_day=false → time NULL (043 CHECK); true → "HH:MM".
    const updates = changed.map((d) => ({
      day_of_week: d.day_of_week,
      is_working_day: d.is_working_day,
      start_time: d.is_working_day ? toHHMM(d.start_time) : null,
      end_time: d.is_working_day ? toHHMM(d.end_time) : null,
    }));
    if (updates.length === 0) return;

    // Client-side: çalışma günü saat aralığı geçerli mi (backend de 400 verir).
    const invalid = updates.some(
      (u) => u.is_working_day && (!u.start_time || !u.end_time || u.start_time >= u.end_time)
    );
    if (invalid) {
      setSaveError('Çalışma günlerinde geçerli bir saat aralığı girin (başlangıç < bitiş).');
      return;
    }

    setSaving(true);
    setSaveError('');
    setSaveOk(false);
    try {
      const d = await updateWorkingHours(updates);
      setDays(d.working_hours);
      setOriginal(d.working_hours);
      setSaveOk(true);
    } catch (err) {
      const data = err.response?.data;
      const status = err.response?.status;
      if (status === 403) {
        setSaveError(data?.error || 'Bu işlem için yetkiniz yok');
      } else if (data?.errors) {
        setSaveError(data.errors[0]?.msg || 'Geçersiz veri');
      } else {
        setSaveError(data?.error || 'Kaydedilemedi');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Çalışma Saatleri</h1>
        <p className="text-slate-500 text-sm mt-1">
          Şirketin standart çalışma saatleri. Bu saatler ileride mesai başlatma
          hatırlatması ve mesai dışı işaretleme için kullanılacaktır.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : loadError ? (
        <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{loadError}</p>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-slate-200 px-5 py-2">
            {DISPLAY_ORDER.map((dow) => {
              const day = days.find((d) => d.day_of_week === dow);
              return day ? (
                <WorkingHoursDayRow
                  key={dow}
                  day={day}
                  onChange={handleDayChange}
                  disabled={!isAdmin}
                />
              ) : null;
            })}
          </div>

          <p className="text-xs text-slate-400">
            Saat dilimi şube bazında tanımlanır — Şubeler sayfasından yönetilir.
          </p>

          {saveError && (
            <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{saveError}</p>
          )}
          {saveOk && (
            <p className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
              Çalışma saatleri kaydedildi.
            </p>
          )}

          {isAdmin && (
            <div className="flex justify-end">
              <Button onClick={handleSave} loading={saving} disabled={changed.length === 0}>
                Kaydet
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
